import { projectsBillingRecipientSnapshotCapability } from '@crest/module-projects/contracts/billing-recipient';
import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { platformOptionalFactProviderHostCapability, platformPortalAccessScopeProviderHostCapability, platformPortalHostCapability, } from '@crest/platform-contracts/hosts';
import { platformStorageObjectReferenceCapability } from '@crest/platform-contracts/storage';
import { projectsProjectReferenceAccessHoldCapability, projectsProjectReferenceCapability, } from '@crest/module-projects/contracts/project-reference';
import { financeInsuranceClaimsBillingCapability, financeInsuranceClaimsBillingProviderId, } from './contracts/finance-insurance-claims-billing.contract.js';
import { financeInsuranceClaimsScopeBillingCapability, financeInsuranceClaimsScopeBillingProviderId, } from './contracts/finance-insurance-claims-scope-billing.contract.js';
import { financeInvoiceLifecycleCapability, financePaymentsCapability, } from './contracts/finance-invoice-lifecycle.contract.js';
import { financeInvoiceContentCapability } from './contracts/finance-invoice-content.contract.js';
import { financeEstimatingBillableRevisionsCapability, } from './contracts/finance-estimating-billable-revision.contract.js';
import { financeExpensesCapability, financeReceiptAnalysisCapability, } from './contracts/finance-expense.contract.js';
import { financeOverheadCapability } from './contracts/finance-overhead.contract.js';
import { financeReconciliationCapability } from './contracts/finance-reconciliation.contract.js';
import { financeReconciliationAdaptersCapability } from './contracts/finance-reconciliation-adapter.contract.js';
import { financePaymentReconciliationCapability } from './contracts/finance-payment-reconciliation.contract.js';
import { financeProjectProfitabilityCapability } from './contracts/finance-project-profitability.contract.js';
import { financeImportCapability } from './contracts/finance-import.contract.js';
import { financeReferralCommissionAttributionsCapability, financeReferralCommissionCapability, financeReferralCommissionCostsCapability, } from './contracts/finance-referral-commission.contract.js';
import { financeReportingProjectFinancialsCapability, financeReportingProjectFinancialsProviderId, } from './contracts/finance-reporting-project-financials.contract.js';
import { financePortalInvoiceScopeProviderId, financePortalInvoicesCapability, financePortalInvoicesReadGrant, } from './contracts/finance-portal-invoice.contract.js';
export const financeModuleManifest = defineCrestModule({
    id: 'finance',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        projectsBillingRecipientSnapshotCapability,
        projectsProjectReferenceCapability,
        projectsProjectReferenceAccessHoldCapability,
        'projects.finance-profitability-index.v1',
        platformOptionalFactProviderHostCapability,
        platformPortalAccessScopeProviderHostCapability,
        platformPortalHostCapability,
    ],
    optionalCapabilities: [
        'timesheets.payroll-cost-summary.v1',
        'communications.email-delivery.v1',
        financeEstimatingBillableRevisionsCapability,
        platformStorageObjectReferenceCapability,
        'timesheets.finance-profitability-labor.v1',
        'assets.finance-profitability-equipment.v1',
        'procurement.finance-profitability-costs.v1',
        'estimating.finance-profitability-contract-values.v1',
        financeReferralCommissionAttributionsCapability,
    ],
    optionalCapabilityFallbacks: [
        {
            capability: 'timesheets.payroll-cost-summary.v1',
            degradedMode: 'finance.degraded.manual-labor-costing.v1',
        },
        {
            capability: 'communications.email-delivery.v1',
            degradedMode: 'finance.degraded.provider-neutral-invoice.v1',
        },
        {
            capability: financeEstimatingBillableRevisionsCapability,
            degradedMode: 'finance.degraded.manual-invoice-content.v1',
        },
        {
            capability: platformStorageObjectReferenceCapability,
            degradedMode: 'finance.degraded.manual-expenses.v1',
        },
        {
            capability: 'timesheets.finance-profitability-labor.v1',
            degradedMode: 'finance.degraded.profitability-labor-unavailable.v1',
        },
        {
            capability: 'assets.finance-profitability-equipment.v1',
            degradedMode: 'finance.degraded.profitability-equipment-unavailable.v1',
        },
        {
            capability: 'procurement.finance-profitability-costs.v1',
            degradedMode: 'finance.degraded.profitability-procurement-unavailable.v1',
        },
        {
            capability: 'estimating.finance-profitability-contract-values.v1',
            degradedMode: 'finance.degraded.profitability-contract-value-unavailable.v1',
        },
        {
            capability: financeReferralCommissionAttributionsCapability,
            degradedMode: 'finance.degraded.referral-commissions-unavailable.v1',
        },
    ],
    provides: [
        'finance.invoices.v1',
        financeInvoiceLifecycleCapability,
        financePaymentsCapability,
        financeInvoiceContentCapability,
        financeExpensesCapability,
        financeReceiptAnalysisCapability,
        financeOverheadCapability,
        financeReconciliationCapability,
        financeReconciliationAdaptersCapability,
        financePaymentReconciliationCapability,
        financeProjectProfitabilityCapability,
        financeReferralCommissionCapability,
        financeReferralCommissionCostsCapability,
        financeInsuranceClaimsBillingCapability,
        financeInsuranceClaimsScopeBillingCapability,
        financeImportCapability,
        financeReportingProjectFinancialsCapability,
        financePortalInvoicesCapability,
    ],
    permissions: [
        'finance.invoices.read',
        'finance.invoices.create',
        'finance.invoices.manage',
        'finance.invoice-writeoffs.manage',
        'finance.payments.read',
        'finance.payments.record',
        'finance.payments.complete',
        'finance.payments.reverse',
        'finance.payments.reconcile',
        'finance.expenses.read',
        'finance.expenses.read-all',
        'finance.expenses.create',
        'finance.expenses.edit',
        'finance.expenses.manage',
        'finance.expenses.approve',
        'finance.expenses.void',
        'finance.expenses.amounts.read',
        'finance.receipt-analysis.manage',
        'finance.overhead.read',
        'finance.overhead.manage',
        'finance.overhead.lock',
        'finance.reconciliation.read',
        'finance.reconciliation.import',
        'finance.reconciliation.review',
        'finance.reconciliation.close',
        'finance.reconciliation.receipts.request',
        'finance.profitability.read',
        'finance.commissions.read',
        'finance.commissions.rules.manage',
        'finance.commissions.calculate',
        'finance.commissions.approve',
        'finance.commissions.pay',
        'finance.commissions.void',
        'finance.import.read',
        'finance.import.execute',
        'finance.import.rollback',
        financePortalInvoicesReadGrant,
    ],
    settings: [
        'finance.invoices.configuration',
        'finance.invoices.display',
        'finance.copy.invoices',
        'finance.copy.invoice-lifecycle',
        'finance.copy.invoice-content',
        'finance.expenses.configuration',
        'finance.expenses.display',
        'finance.copy.expenses',
        'finance.overhead.display',
        'finance.copy.overhead',
        'finance.reconciliation.configuration',
        'finance.reconciliation.display',
        'finance.copy.reconciliation',
        'finance.payments.configuration',
        'finance.payments.display',
        'finance.copy.payments',
        'finance.profitability.configuration',
        'finance.profitability.display',
        'finance.copy.profitability',
        'finance.commissions.configuration',
        'finance.commissions.display',
        'finance.copy.commissions',
        'finance.portal-invoices.display',
        'finance.copy.portal-invoices',
    ],
    copyCatalogs: [
        'finance.copy.invoices',
        'finance.copy.invoice-lifecycle',
        'finance.copy.invoice-content',
        'finance.copy.expenses',
        'finance.copy.overhead',
        'finance.copy.reconciliation',
        'finance.copy.payments',
        'finance.copy.profitability',
        'finance.copy.commissions',
        'finance.copy.portal-invoices',
    ],
    events: {
        publishes: [
            'finance.invoice-draft-created.v1',
            'finance.invoice-revised.v1',
            'finance.invoice-content-revised.v1',
            'finance.invoice-created-from-estimate.v1',
            'finance.invoice-issued.v1',
            'finance.invoice-voided.v1',
            'finance.invoice-written-off.v1',
            'finance.payment-recorded.v1',
            'finance.payment-completed.v1',
            'finance.payment-reversed.v1',
            'finance.expense-created.v1',
            'finance.expense-revised.v1',
            'finance.expense-approved.v1',
            'finance.expense-rejected.v1',
            'finance.expense-voided.v1',
            'finance.expense-analysis-requested.v1',
            'finance.expense-analysis-recorded.v1',
            'finance.expense-analysis-failed.v1',
            'finance.overhead-cost-center-changed.v1',
            'finance.overhead-account-category-changed.v1',
            'finance.overhead-bill-changed.v1',
            'finance.overhead-bill-occurrence-changed.v1',
            'finance.overhead-allocation-policy-changed.v1',
            'finance.overhead-allocation-run-created.v1',
            'finance.reconciliation-statement-imported.v1',
            'finance.reconciliation-statement-changed.v1',
            'finance.reconciliation-transaction-resolved.v1',
            'finance.reconciliation-transaction-reopened.v1',
            'finance.reconciliation-receipt-request-prepared.v1',
            'finance.payment-processor-entry-recorded.v1',
            'finance.payment-payout-recorded.v1',
            'finance.payment-payout-status-changed.v1',
            'finance.payment-payout-allocated.v1',
            'finance.payment-payout-allocation-reversed.v1',
            'finance.payment-dispute-recorded.v1',
            'finance.payment-dispute-status-changed.v1',
            'finance.payment-refund-recorded.v1',
            'finance.referral-commission-rule-changed.v1',
            'finance.referral-commission-calculated.v1',
            'finance.referral-commission-payout-changed.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'finance.routes.invoices', priority: 0 },
            { id: 'finance.routes.expenses', priority: 10 },
            { id: 'finance.routes.overhead', priority: 20 },
            { id: 'finance.routes.reconciliation', priority: 30 },
            { id: 'finance.routes.payment-reconciliation', priority: 40 },
            { id: 'finance.routes.project-profitability', priority: 50 },
            { id: 'finance.routes.referral-commissions', priority: 60 },
        ],
        jobs: [],
        subscribers: [],
        slots: [{
                id: financeInsuranceClaimsBillingProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'finance.invoices.read',
                priority: 0,
            }, {
                id: financeInsuranceClaimsScopeBillingProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'finance.invoices.read',
                priority: 10,
            }, {
                id: financeReportingProjectFinancialsProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'finance.profitability.read',
                priority: 20,
            }, {
                id: financePortalInvoiceScopeProviderId,
                requiredCapability: platformPortalAccessScopeProviderHostCapability,
                requiredPermission: 'finance.invoices.read',
                priority: 20,
            }],
    },
    web: {
        routes: [
            { id: 'finance.web.invoices', priority: 0 },
            { id: 'finance.web.expenses', priority: 10 },
            { id: 'finance.web.overhead', priority: 20 },
            { id: 'finance.web.reconciliation', priority: 30 },
            { id: 'finance.web.payments', priority: 40 },
            { id: 'finance.web.project-profitability', priority: 50 },
            { id: 'finance.web.referral-commissions', priority: 60 },
        ],
        navigation: [
            { id: 'finance.navigation.invoices', priority: 40 },
            { id: 'finance.navigation.expenses', priority: 45 },
            { id: 'finance.navigation.overhead', priority: 50 },
            { id: 'finance.navigation.reconciliation', priority: 55 },
            { id: 'finance.navigation.payments', priority: 60 },
            { id: 'finance.navigation.project-profitability', priority: 65 },
            { id: 'finance.navigation.referral-commissions', priority: 70 },
        ],
        slots: [{
                id: 'finance.web.projects.billing-summary',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'finance.invoices.read',
                priority: 40,
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: {
        routes: [],
        slots: [{
                id: 'finance.portal.project-invoices',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: financePortalInvoicesReadGrant,
                priority: 40,
            }],
    },
    database: {
        schema: 'finance',
        owns: [
            'finance.invoice_number_counters',
            'finance.invoices',
            'finance.invoice_line_items',
            'finance.invoice_content_versions',
            'finance.invoice_content_sources',
            'finance.invoice_command_receipts',
            'finance.invoice_events',
            'finance.payments',
            'finance.payment_events',
            'finance.invoice_writeoffs',
            'finance.create_invoice_draft',
            'finance.execute_invoice_command',
            'finance.revise_invoice_content',
            'finance.normalize_invoice_content',
            'finance.post_invoice_payment',
            'finance.complete_invoice_payment',
            'finance.reverse_invoice_payment',
            'finance.enforce_invoice_identity_snapshot',
            'finance.enforce_payment_identity',
            'finance.reject_immutable_ledger_change',
            'finance.expenses',
            'finance.expense_versions',
            'finance.expense_events',
            'finance.expense_command_receipts',
            'finance.receipt_analysis_requests',
            'finance.receipt_analysis_results',
            'finance.current_membership_can_read_expense',
            'finance.current_membership_can_read_expense_amounts',
            'finance.current_expense_write_permission',
            'finance.validate_expense_receipt_references',
            'finance.normalize_expense_content',
            'finance.write_expense_version',
            'finance.expense_json',
            'finance.get_expense',
            'finance.list_expenses',
            'finance.get_expense_command_receipt',
            'finance.execute_expense_command',
            'finance.request_receipt_analysis',
            'finance.record_receipt_analysis_result',
            'finance.record_receipt_analysis_failure',
            'finance.overhead_cost_centers',
            'finance.overhead_account_categories',
            'finance.overhead_bills',
            'finance.overhead_bill_occurrences',
            'finance.overhead_allocation_policies',
            'finance.overhead_allocation_runs',
            'finance.overhead_allocation_items',
            'finance.overhead_entity_versions',
            'finance.overhead_events',
            'finance.overhead_command_receipts',
            'finance.current_membership_can_read_overhead',
            'finance.overhead_entity_json',
            'finance.get_overhead_entity',
            'finance.get_overhead_allocation_policy',
            'finance.list_overhead_entities',
            'finance.execute_overhead_entity_command',
            'finance.overhead_allocation_preview',
            'finance.get_overhead_allocation_run',
            'finance.list_overhead_allocation_runs',
            'finance.create_overhead_allocation_run',
            'finance.get_overhead_dashboard',
            'finance.get_overhead_forecast',
            'finance.reconciliation_statements',
            'finance.reconciliation_transactions',
            'finance.reconciliation_match_candidates',
            'finance.reconciliation_decisions',
            'finance.reconciliation_statement_versions',
            'finance.reconciliation_events',
            'finance.reconciliation_command_receipts',
            'finance.reconciliation_receipt_request_intents',
            'finance.current_membership_can_read_reconciliation',
            'finance.reconciliation_statement_summary',
            'finance.reconciliation_statement_json',
            'finance.reconciliation_transaction_json',
            'finance.reconciliation_target_snapshot',
            'finance.write_reconciliation_statement_version',
            'finance.get_reconciliation_statement',
            'finance.get_reconciliation_statement_detail',
            'finance.get_reconciliation_transaction',
            'finance.list_reconciliation_statements',
            'finance.get_reconciliation_dashboard',
            'finance.execute_reconciliation_import',
            'finance.execute_reconciliation_transaction_command',
            'finance.execute_reconciliation_statement_command',
            'finance.reconciliation_receipt_request_intent_json',
            'finance.get_reconciliation_receipt_request_intent',
            'finance.prepare_reconciliation_receipt_request',
            'finance.payment_processor_entries',
            'finance.payment_payouts',
            'finance.payment_payout_allocations',
            'finance.payment_disputes',
            'finance.payment_refunds',
            'finance.payment_reconciliation_events',
            'finance.payment_reconciliation_command_receipts',
            'finance.current_membership_can_read_payment_reconciliation',
            'finance.current_payment_bank_clearings',
            'finance.record_payment_processor_entry',
            'finance.record_payment_payout',
            'finance.update_payment_payout_status',
            'finance.allocate_payment_payout',
            'finance.reverse_payment_payout_allocation',
            'finance.record_payment_dispute',
            'finance.update_payment_dispute_status',
            'finance.record_payment_refund',
            'finance.enforce_payment_payout_lifecycle',
            'finance.enforce_payment_payout_allocation_lifecycle',
            'finance.enforce_payment_dispute_lifecycle',
            'finance.read_project_profitability_facts',
            'finance.referral_commission_rules',
            'finance.referral_commission_rule_versions',
            'finance.referral_commission_generations',
            'finance.referral_commission_payouts',
            'finance.referral_commission_events',
            'finance.referral_commission_command_receipts',
            'finance.referral_commission_rule_json',
            'finance.referral_commission_payout_json',
            'finance.get_referral_commission_rule',
            'finance.get_referral_commission_payout',
            'finance.list_referral_commission_rules',
            'finance.list_referral_commission_payouts',
            'finance.list_referral_commission_generation_payouts',
            'finance.execute_referral_commission_rule_command',
            'finance.referral_commission_project_revenue_cents',
            'finance.execute_referral_commission_calculation',
            'finance.execute_referral_commission_payout_command',
            'finance.read_referral_commission_costs',
            'finance.import_domain_totals_valid',
            'finance.import_runs',
            'finance.import_identity_map',
            'finance.import_row_receipts',
            'finance.import_rejects',
            'finance.import_rollback_receipts',
            'finance.water_pro_money_import_target_changes',
            'finance.water_pro_money_import_target_rollbacks',
            'finance.begin_import_run',
            'finance.record_import_reject',
            'finance.record_import_receipt',
            'finance.complete_import_run',
            'finance.fail_import_run',
            'finance.mark_import_run_rolled_back',
            'finance.get_import_run',
            'finance.get_import_run_evidence',
            'finance.apply_water_pro_money_import_v1',
            'finance.verify_water_pro_money_import_receipt_v1',
            'finance.rollback_water_pro_money_import_v1',
            'finance.read_portal_invoices',
        ],
        readsThrough: [
            projectsBillingRecipientSnapshotCapability,
            financeEstimatingBillableRevisionsCapability,
            projectsProjectReferenceCapability,
            projectsProjectReferenceAccessHoldCapability,
            platformStorageObjectReferenceCapability,
            'platform.membership-directory.v1',
            'projects.finance-profitability-index.v1',
            'timesheets.finance-profitability-labor.v1',
            'assets.finance-profitability-equipment.v1',
            'procurement.finance-profitability-costs.v1',
            'estimating.finance-profitability-contract-values.v1',
            financeReferralCommissionAttributionsCapability,
        ],
        migrations: [
            'finance.invoice_golden_slice',
            'finance.scope-source-evidence',
            'finance.invoice-lifecycle-payments',
            'finance.invoice-content-revisions',
            'finance.expense-receipt-foundation',
            'finance.overhead-foundation',
            'finance.reconciliation-foundation',
            'finance.reconciliation-operational-commands',
            'finance.payment-provider-reconciliation',
            'finance.project-profitability',
            'finance.referral-commissions',
            'finance.import-reconciliation',
            'finance.water-pro-money-import',
            'finance.portal-invoice-readback',
            'finance.project-reference-mutation-holds',
        ],
    },
    health: ['finance.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'The current Finance slice publishes facts but owns no inbound event subscriber.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['finance.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Finance ledgers, import identity/quarantine/rollback evidence, and reconciliation records remain inaccessible while disabled; deletion needs separate authority.',
});
//# sourceMappingURL=finance.module.js.map